import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "*" | "/";

export function App() {
	const [display, setDisplay] = useState("0");
	const [previousValue, setPreviousValue] = useState<number | null>(null);
	const [operator, setOperator] = useState<Operator | null>(null);
	const [waitingForOperand, setWaitingForOperand] = useState(false);

	const inputDigit = useCallback(
		(digit: string) => {
			if (waitingForOperand) {
				setDisplay(digit);
				setWaitingForOperand(false);
			} else {
				setDisplay(display === "0" ? digit : display + digit);
			}
		},
		[display, waitingForOperand],
	);

	const inputDecimal = useCallback(() => {
		if (waitingForOperand) {
			setDisplay("0.");
			setWaitingForOperand(false);
			return;
		}
		if (!display.includes(".")) {
			setDisplay(display + ".");
		}
	}, [display, waitingForOperand]);

	const clear = useCallback(() => {
		setDisplay("0");
		setPreviousValue(null);
		setOperator(null);
		setWaitingForOperand(false);
	}, []);

	const toggleSign = useCallback(() => {
		const value = parseFloat(display);
		if (value !== 0) {
			setDisplay(String(-value));
		}
	}, [display]);

	const inputPercent = useCallback(() => {
		const value = parseFloat(display);
		setDisplay(String(value / 100));
	}, [display]);

	const calculate = useCallback(
		(nextOperator?: Operator) => {
			const currentValue = parseFloat(display);

			if (previousValue !== null && operator) {
				let result: number;
				switch (operator) {
					case "+":
						result = previousValue + currentValue;
						break;
					case "-":
						result = previousValue - currentValue;
						break;
					case "*":
						result = previousValue * currentValue;
						break;
					case "/":
						result = currentValue === 0 ? 0 : previousValue / currentValue;
						break;
				}

				const resultStr =
					Math.abs(result) < 1e15
						? String(parseFloat(result.toPrecision(12)))
						: result.toExponential(6);

				setDisplay(resultStr);
				setPreviousValue(nextOperator ? parseFloat(resultStr) : null);
			} else {
				setPreviousValue(nextOperator ? currentValue : null);
			}

			setOperator(nextOperator ?? null);
			setWaitingForOperand(true);
		},
		[display, previousValue, operator],
	);

	const formatDisplay = (value: string) => {
		if (value.includes("e")) return value;
		const parts = value.split(".");
		const intPart = parts[0];
		const sign = intPart.startsWith("-") ? "-" : "";
		const digits = intPart.replace("-", "");
		const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		return sign + formatted + (parts.length > 1 ? "." + parts[1] : "");
	};

	const displayFontSize =
		display.length > 12
			? "text-2xl"
			: display.length > 9
				? "text-3xl"
				: display.length > 6
					? "text-4xl"
					: "text-5xl";

	const operatorButtons: { label: string; op: Operator }[] = [
		{ label: "\u00F7", op: "/" },
		{ label: "\u00D7", op: "*" },
		{ label: "\u2212", op: "-" },
		{ label: "+", op: "+" },
	];

	return (
		<div className="min-h-screen flex items-center justify-center bg-background p-4">
			<Card className="w-full max-w-xs shadow-2xl border-border/50 overflow-hidden">
				<CardContent className="p-0">
					{/* Display */}
					<div className="bg-muted/30 px-5 pt-8 pb-4 min-h-[100px] flex items-end justify-end">
						<span
							className={`${displayFontSize} font-light tracking-tight text-foreground truncate`}
						>
							{formatDisplay(display)}
						</span>
					</div>

					{/* Buttons */}
					<div className="grid grid-cols-4 gap-px bg-border/30 p-px">
						{/* Row 1: AC, +/-, %, ÷ */}
						<Button
							variant="secondary"
							className="h-16 rounded-none text-lg font-medium"
							onClick={clear}
						>
							AC
						</Button>
						<Button
							variant="secondary"
							className="h-16 rounded-none text-lg font-medium"
							onClick={toggleSign}
						>
							+/&minus;
						</Button>
						<Button
							variant="secondary"
							className="h-16 rounded-none text-lg font-medium"
							onClick={inputPercent}
						>
							%
						</Button>
						<Button
							className={`h-16 rounded-none text-lg font-medium ${
								operator === "/" && waitingForOperand
									? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
									: "bg-orange-500 text-white hover:bg-orange-400"
							}`}
							onClick={() => calculate("/")}
						>
							&divide;
						</Button>

						{/* Row 2: 7, 8, 9, × */}
						{["7", "8", "9"].map((d) => (
							<Button
								key={d}
								variant="outline"
								className="h-16 rounded-none text-lg font-medium border-0 bg-card hover:bg-accent"
								onClick={() => inputDigit(d)}
							>
								{d}
							</Button>
						))}
						<Button
							className={`h-16 rounded-none text-lg font-medium ${
								operator === "*" && waitingForOperand
									? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
									: "bg-orange-500 text-white hover:bg-orange-400"
							}`}
							onClick={() => calculate("*")}
						>
							&times;
						</Button>

						{/* Row 3: 4, 5, 6, - */}
						{["4", "5", "6"].map((d) => (
							<Button
								key={d}
								variant="outline"
								className="h-16 rounded-none text-lg font-medium border-0 bg-card hover:bg-accent"
								onClick={() => inputDigit(d)}
							>
								{d}
							</Button>
						))}
						<Button
							className={`h-16 rounded-none text-lg font-medium ${
								operator === "-" && waitingForOperand
									? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
									: "bg-orange-500 text-white hover:bg-orange-400"
							}`}
							onClick={() => calculate("-")}
						>
							&minus;
						</Button>

						{/* Row 4: 1, 2, 3, + */}
						{["1", "2", "3"].map((d) => (
							<Button
								key={d}
								variant="outline"
								className="h-16 rounded-none text-lg font-medium border-0 bg-card hover:bg-accent"
								onClick={() => inputDigit(d)}
							>
								{d}
							</Button>
						))}
						<Button
							className={`h-16 rounded-none text-lg font-medium ${
								operator === "+" && waitingForOperand
									? "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
									: "bg-orange-500 text-white hover:bg-orange-400"
							}`}
							onClick={() => calculate("+")}
						>
							+
						</Button>

						{/* Row 5: 0 (wide), ., = */}
						<Button
							variant="outline"
							className="h-16 rounded-none text-lg font-medium border-0 bg-card hover:bg-accent col-span-2 text-left pl-7"
							onClick={() => inputDigit("0")}
						>
							0
						</Button>
						<Button
							variant="outline"
							className="h-16 rounded-none text-lg font-medium border-0 bg-card hover:bg-accent"
							onClick={inputDecimal}
						>
							.
						</Button>
						<Button
							className="h-16 rounded-none text-lg font-medium bg-orange-500 text-white hover:bg-orange-400"
							onClick={() => calculate()}
						>
							=
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export default App;
